import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React from 'react';

import { Button, Stack, SxProps, Typography, useTheme } from '@mui/material';

import { ISite } from '../models/ISite';
import {
  isInitializingAtom,
  navbarHeightAtom,
  refinersExpandedAtom,
  selectedLocationIdAtom,
  selectedRefinerItemsAtom,
  selectedSiteAtom,
  sitesAtom,
} from '../utils/AtomConfig';
import Constants from '../utils/Constants';
import { ComponentLocation } from '../models/ComponentLocation';

export function Sites(props: { componentLocation: ComponentLocation }) {
  const theme = useTheme();
  
  const selectedLocationId = useAtomValue(selectedLocationIdAtom);
  const sites = useAtomValue(sitesAtom);
  const [selectedSite, setSelectedSite] = useAtom(selectedSiteAtom);
  const setIsInitializing = useSetAtom(isInitializingAtom);
  const setSelectedRefinerItems = useSetAtom(selectedRefinerItemsAtom);
  const navbarHeight = useAtomValue(navbarHeightAtom);
  const setRefinersExpanded = useSetAtom(refinersExpandedAtom);

  function isSelectedSite(siteId: number) {
    return selectedSite && selectedSite.Id === siteId;
  }

  function updateSelectedSite(site: ISite) {
    if (selectedSite?.Id === site.Id) return;

    if (site.Title === 'Home') {
      setRefinersExpanded(false);
    } else {
      setRefinersExpanded(true);
    }

    setSelectedRefinerItems([]);
    setIsInitializing(true);
    setSelectedSite(site);
  }

  const isLocationSelected = selectedLocationId !== Constants.DEFAULT_ID;
  const mainStyle: SxProps = {
    position: 'fixed',
    top: navbarHeight,
    bottom: 0,
    left: 0,
    backgroundColor: '#FFFFFF',
    width: '240px',
    overflowY: 'auto',
    boxShadow: '0px 0px 10px rgb(0 0 0 / 30%)',
    zIndex: 1,
    overflowX: 'hidden',
    '& button': {
      borderRadius: 0,
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(3),
      textAlign: 'left',
      display: 'block',
      '&.active, &.active:hover': {
        backgroundColor: 'rgba(0,0,0,0.15)',
      },
      '&:not(.active):hover': {
        backgroundColor: 'rgba(0,0,0,0.08)',
      },
    },
    display: {
      xs: 'none',
      lg: 'flex',
    },
  };

  const navBarStyle: SxProps = {
    position: 'relative',
    backgroundColor: '#FFFFFF',
    overflowY: 'auto',
    boxShadow:
      '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
    marginTop: '10px',
    zIndex: 1,
    minHeight: '250px',
    overflowX: 'hidden',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
    marginBottom: 4,
    '& button': {
      borderRadius: 0,
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(3),
      textAlign: 'left',
      display: 'block',
      '&.active, &.active:hover': {
        backgroundColor: 'rgba(0,0,0,0.15)',
      },
      '&:not(.active):hover': {
        backgroundColor: 'rgba(0,0,0,0.08)',
      },
    },
    display: {
      xs: 'flex',
      lg: 'none',
    },
  };

  const filteredSites = sites
    .filter((site) => site.LocationId === selectedLocationId)
    .sort((a, b) => {
      if (a.Title === 'Home') {
        return -1;
      }
      if (b.Title === 'Home') {
        return 1;
      }
      return a.Title.localeCompare(b.Title);
    });

  return (
    <>
      {props.componentLocation === 'drawer' && (
        <Typography component='h2' variant='h5' sx={{ color: '#003591' }}>
          Sites
        </Typography>
      )}

      <Stack
        direction='column'
        sx={props.componentLocation === 'drawer' ? navBarStyle : mainStyle}
      >
        {/* Show message if location is not selected */}
        {!isLocationSelected && (
          <Typography align='center' color='black' marginTop={2}>
            Please select location first
          </Typography>
        )}

        {filteredSites.map((site, index) => (
          <Button
            key={site.Id}
            className={isSelectedSite(site.Id) ? 'active' : ''}
            onClick={() => updateSelectedSite(site)}
          >
            {site.Title}
          </Button>
        ))}
      </Stack>
    </>
  );
}
